import { useRouter, useRuntimeConfig } from '#imports'
import { FetchError } from 'ofetch'
import type { RouteLocationAsRelativeGeneric } from 'vue-router'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { clearAuthStorages } from '../utils/clearAuthStorage'

import { useAuthOrigin } from './useAuthOrigin'
import { useRedirectLocale } from './useRedirectLocale'
import { useUserStore } from './useUserStore'

/*
 * Log the current user out.
 *
 * This functions takes care of logging people out from everywhere (Badoom,
 * the Identity stack, etc), and clears the different internal storages. In
 * the end, people get redirect to the default location given as part of
 * the module configuration.
 *
 * @example
 * const { logout } = useLogout()
 *
 * logout() // Call this whenever you want.
 */
export function useLogout() {
  const router = useRouter()
  const config = useRuntimeConfig().public.oauth
  const $httpFetch = useHttpFetch()
  const { $reset } = useUserStore()

  async function logout(redirection?: RouteLocationAsRelativeGeneric) {
    try {
      await $httpFetch('/bm/client/logout', {
        method: 'POST',
      })
      await $httpFetch('/auth/logout', { baseURL: useAuthOrigin() })
    } catch (error) {
      // This can happen for example when multiple tabs are open and you log out from one of them.
      const isUserNotLoggedIn =
        error instanceof FetchError && error.status === 401

      if (isUserNotLoggedIn) {
        // We want to continue
      } else {
        throw error
      }
    }

    $reset()

    await clearAuthStorages()

    const { getRouteLocationWithLocale } = useRedirectLocale()

    // Force a reload to clear the in-memory data (stores, components, etc).
    const redirect = router.resolve(
      redirection ||
        getRouteLocationWithLocale(config.defaultRedirectLocationWhenGuest),
    )
    window.location.assign(redirect.href)
  }

  return { logout }
}
